import { useState, useCallback, useRef } from 'react';
import { Chat, Chip, Icon } from '@iqueue/iq-ui';
import { api } from '../../lib/api';

const MAX_UPLOAD = 25 * 1024 * 1024;

/** The pill says what KIND of file this is when there is no thumbnail. */
function mimeIcon(mime, name) {
  const m = mime || '';
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('audio/')) return 'music_note';
  if (m.startsWith('video/')) return 'movie';
  if (m === 'application/pdf') return 'picture_as_pdf';
  if (/zip|tar|gzip|compressed/.test(m)) return 'folder_zip';
  if (/^text\/|json|javascript|xml/.test(m) || /\.(js|jsx|ts|tsx|py|sh|json|md|ya?ml)$/i.test(name || '')) return 'description';
  return 'attach_file';
}

/** Size is locale work, so the app formats it (Chat.Attachment `size`). */
function formatSize(bytes) {
  if (!(bytes >= 0)) return undefined;
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  let value = bytes, i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1; }
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: value < 10 && i > 0 ? 1 : 0 })} ${units[i]}`;
}

/**
 * Composer state + parts on top of iq-ui Chat.Composer, with our @mention
 * autocomplete and a reply-to bar.
 *
 * A HOOK returning elements rather than a component: Chat.Root buckets its
 * parts by element identity, so the <Chat.Composer> element must land in
 * Chat.Root's children literally — wrapped in a component of ours it would
 * fall into the flow region and lose the bottom dock.
 *
 * The mention picker rides Chat.Suggestions — the one surface that docks
 * directly above the draft. The library documents a listbox as the right shape
 * for it (draftProps carries the ARIA), but offers no anchored surface to put
 * one in: an unknown Chat.Root child lands in the flow ABOVE the log and an
 * unknown Composer child lands in the ACTION ROW beside send. Gap filed in
 * docs/IQ_UI_CHAT_GAPS.md §1; until it closes, placement wins over role.
 *
 * The pick is written back with draftRef.replaceRange, so a mention lands where
 * it was typed instead of being appended at the end of the draft.
 */
export function useChatComposer({ onSend, members, currentRoom, replyTo, onCancelReply }) {
  const [draft, setDraft] = useState('');
  // { uid, file, name, mime, size, status?: 'uploading'|'error', artifactId?, src?, tooBig? }
  const [attached, setAttached] = useState([]);
  const [mention, setMention] = useState(null); // { query, start, end }
  const [activeIndex, setActiveIndex] = useState(0);
  const draftRef = useRef(null);
  const uidRef = useRef(0);

  const candidates = mention
    ? (members || [])
      .filter(m => m.participant.toLowerCase().startsWith(mention.query.toLowerCase()))
      .slice(0, 8)
    : [];
  const open = candidates.length > 0;
  const active = open ? candidates[Math.min(activeIndex, candidates.length - 1)] : null;

  /** The @token the caret sits in, or null. Read from the caret, not the end of the draft. */
  const syncMention = useCallback((value) => {
    const sel = draftRef.current && draftRef.current.getSelection && draftRef.current.getSelection();
    const caret = sel && typeof sel.end === 'number' ? sel.end : value.length;
    const m = value.slice(0, caret).match(/(^|[^\w@])@([a-zA-Z0-9_-]*)$/);
    if (!m) { setMention(null); return; }
    setMention({ query: m[2], start: caret - m[2].length - 1, end: caret });
    setActiveIndex(0);
  }, []);

  const handleValueChange = useCallback((value) => {
    setDraft(value);
    syncMention(value);
  }, [syncMention]);

  const pick = useCallback((participant) => {
    const text = `@${participant} `;
    const handle = draftRef.current;
    if (handle && handle.replaceRange && mention) {
      handle.replaceRange(mention.start, mention.end, text);
    } else {
      setDraft(prev => prev.replace(/@([a-zA-Z0-9_-]*)$/, text));
    }
    setMention(null);
    if (handle && handle.focus) handle.focus();
  }, [mention]);

  const extractMentions = useCallback((value) => {
    const out = [];
    const seen = new Set();
    for (const m of value.matchAll(/(^|[^\w@])@([a-zA-Z0-9][a-zA-Z0-9_-]{0,63})/g)) {
      const h = m[2].toLowerCase();
      if (!seen.has(h)) { seen.add(h); out.push({ participant: m[2] }); }
    }
    return out;
  }, []);

  const handleSend = useCallback(() => {
    const value = draft.trim();
    if (!value && !attached.length) return;
    if (attached.some(a => a.status === 'uploading')) return;
    onSend({
      text: value,
      mentions: extractMentions(value),
      attachments: attached.map(a => a.artifactId).filter(Boolean),
      replyToMessageId: replyTo ? replyTo.message_id : null,
    });
    setDraft('');
    setAttached([]);
    setMention(null);
    if (onCancelReply) onCancelReply();
  }, [draft, attached, onSend, extractMentions, replyTo, onCancelReply]);

  // Keys the composer claims (Enter) reach us BEFORE it acts on them; while the
  // picker is open Enter picks instead of sending.
  const handleDraftKeyDown = useCallback((e) => {
    if (!open) return;
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      if (active) pick(active.participant);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => (i + 1) % candidates.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => (i - 1 + candidates.length) % candidates.length);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setMention(null);
    }
  }, [open, active, candidates.length, pick]);

  // The highlighted candidate leads the row, so Enter and the eye agree.
  const suggestions = open ? (
    <Chat.Suggestions
      options={[active, ...candidates.filter(c => c !== active)]
        .filter(Boolean).map(c => `@${c.participant}`)}
      onSelect={(option) => pick(option.replace(/^@/, ''))}
      aria-label="Упоминания участников"
    />
  ) : null;

  // A failed upload STAYS in the strip as status="error" with the pill itself
  // as the retry (Chat.Attachment states) — dropping it silently was the one
  // outcome the person who picked the file cannot see.
  const upload = useCallback(async (entry) => {
    setAttached(prev => prev.map(a => a.uid === entry.uid ? { ...a, status: 'uploading' } : a));
    try {
      const { artifact } = await api.upload(currentRoom, entry.file);
      setAttached(prev => prev.map(a => a.uid === entry.uid ? {
        ...a,
        artifactId: artifact.id,
        name: artifact.name || a.name,
        mime: artifact.mimeType || a.mime,
        size: artifact.size != null ? artifact.size : a.size,
        src: (artifact.mimeType || '').startsWith('image/') ? api.artifactUrl(artifact.id) : undefined,
        status: undefined,
      } : a));
    } catch (e) {
      setAttached(prev => prev.map(a => a.uid === entry.uid ? { ...a, status: 'error' } : a));
    }
  }, [currentRoom]);

  const handleFiles = useCallback((fileList) => {
    const entries = [...fileList].map(file => ({
      uid: ++uidRef.current,
      file,
      name: file.name,
      mime: file.type,
      size: file.size,
      tooBig: file.size > MAX_UPLOAD,
      status: file.size > MAX_UPLOAD ? 'error' : 'uploading',
    }));
    if (!entries.length) return;
    setAttached(prev => [...prev, ...entries]);
    entries.filter(e => !e.tooBig).forEach(upload);
  }, [upload]);

  // The pending reply rides the composer's action row as a removable chip —
  // the only slot inside the composer an app child can reach.
  const replyChip = replyTo ? (
    <Chip
      variant="input"
      leadingIcon={<Icon name="reply" />}
      onRemove={onCancelReply}
      removeAriaLabel="Отменить ответ"
    >
      {replyTo.sender}
    </Chip>
  ) : null;

  const composer = (
    <Chat.Composer
      key="composer"
      value={draft}
      onValueChange={handleValueChange}
      onDraftKeyDown={handleDraftKeyDown}
      draftRef={draftRef}
      draftProps={{ 'aria-autocomplete': 'list' }}
      onSend={handleSend}
      onFiles={handleFiles}
      placeholder="Сообщение..."
      hasAttachments={attached.some(a => a.artifactId)}
      sendLabel="Отправить"
      attachLabel="Прикрепить файл"
    >
      {replyChip}
      {attached.map(f => (
        <Chat.Attachment
          key={f.uid}
          name={f.name}
          size={formatSize(f.size)}
          src={f.src}
          icon={<Icon name={mimeIcon(f.mime, f.name)} />}
          status={f.status}
          statusLabel={f.tooBig ? 'Файл больше 25 МБ' : undefined}
          onRetry={f.status === 'error' && !f.tooBig ? () => upload(f) : undefined}
          onRemove={() => setAttached(prev => prev.filter(a => a.uid !== f.uid))}
        />
      ))}
    </Chat.Composer>
  );

  // onFiles is also handed to Chat.Log: a drop anywhere on the transcript is
  // one of the four ways files arrive, and they all go through this callback.
  return { composer, suggestions, onFiles: handleFiles };
}
